// #genai Phase 2 "Add Credit Card Payment Method" route (/billing-information/add-card).
// Pixel-matched to Figma 269:21301 (empty) / 269:21393 (filled) / 269:21485 (success modal).
// Reuses the Phase 1 PaymentInformation (microform card form) and AddressInformation (billing
// address) verbatim — same tokenize() + submit saga. Per the new design the payment-type chooser
// is gone — the page is single-purpose and renders just two numbered sections:
//   1) Payment Information   (reused PaymentInformation: card fields only, names hidden)
//   2) Billing Information   (First/Last name + reused AddressInformation, internal buttons hidden)
// Footer: Add Payment Method / Cancel. First/Last name live in the Billing
// Information section (PaymentInformation renders card fields only via hideNameFields).
// Gated by the ENABLE_MULTI_PAYMENT_METHODS flag.
import React, {
  useEffect, useState, useRef, useCallback,
} from 'lib/react';
import { Redirect } from 'lib/router';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import {
  EVGrid, EVTypography, EVButton, EVArrowBackIcon,
} from 'lib/components';
import { BILLING_INFORMATION_ROUTE } from 'constants/RouteConstants';
import { CREDIT_CARD_NAMES } from 'constants/AppConstants';
import PaymentInformation from 'commonComponents/paymentInformation';
import { PAYMENT_METHOD_OPTION_IDS } from 'commonComponents/paymentInformation/PaymentInformationConstants';
import TextInputField from 'commonComponents/textInputField';
import AddressInformation from 'commonComponents/addressInformation';
import LabelledCheckbox from 'commonComponents/labelled-checkbox';
import ValidationMessageBanner from 'commonComponents/validationMessageBanner';
import history from 'store/CreateHistory';
import useAddPaymentRouteAccess, { ADD_PAYMENT_ROUTE_ACCESS } from '../useAddPaymentRouteAccess';
import AddPaymentMethodLoader from '../components/AddPaymentMethodLoader/AddPaymentMethodLoader.component';
import {
  updateCardPaymentInfo,
  updateCreditCardConsent,
  updateAddressInfo,
  updateAddressInfoErrors,
  submitBillingInfo,
  setSelectedPaymentMethod,
  resetBillingSubmission,
  resetAddPaymentFormState,
  loadBillingAccountContext,
  setRecentlyAddedPaymentMethod,
  updateAddressValidationErrorsForDisplay, // #genai Bugbot: promote inline field errors to banner
  updateCardPaymentInfoErrors, // #genai Bugbot: route BillToFirstNameMissing / BillToLastNameMissing inline
  updateCardPaymentInfoErrorsForBanner, // #genai Bugbot: banner line item for the same errors
} from '../BillingInformation.actions';
import { buildElementRefInfoState } from '../BillingInformation.component';
import { showValidationBanner, updateMessages, shouldUpdateElementRefMessages } from '../BillingInformation.util';
// #genai Phase 2: shared with the Add-Bank page — same "duplicate / orphan / generic" branching,
// same two dialogs, so both add-payment flows are visually + behaviourally consistent.
import {
  getSubmitErrorDialogKind,
  getBillToInlineFieldError,
  getBillToInlinePaymentFieldError, // #genai Bugbot: BillToFirstNameMissing / BillToLastNameMissing inline routing
} from '../BankAccountInfoErrors';
import PaymentMethodExistsDialog from '../AddAchPaymentMethod/SubmitErrorDialogs/PaymentMethodExistsDialog.component';
import SomethingWentWrongDialog from '../AddAchPaymentMethod/SubmitErrorDialogs/SomethingWentWrongDialog.component';
import AddCardPaymentMethodStyles from './AddCardPaymentMethodStyles';

function AddCardPaymentMethod() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = AddCardPaymentMethodStyles();
  const paymentInfoRef = useRef(null);

  const routeAccess = useAddPaymentRouteAccess();
  // #genai Deep-link guard: account context (billPayerId / initParameters) lives in
  // userData.billingInformation, normally hydrated by the /billing-information page. Hydrate it here
  // when missing so a direct visit to this route still has the account context for the card save.
  const billPayerId = useSelector(({ userData }) => userData.billingInformation?.billPayerId);

  const {
    isLoading,
    // #genai After a successful save on the toggle path (Phase 2 users are ACH-eligible, so the
    // submit saga always takes the toggle branch — see BillingInformation.saga applyCardUpdate…),
    // the saga populates `cardPaymentInfo` from the §2.6 response body via paymentInfoSelector
    // (creditCardType → cardType, creditCardNumber → cardNumber). We read the success banner's
    // brand + last-4 from THIS slice so the banner is server-authoritative and never leaks the
    // legacy `paymentInfo` slice (which the toggle path does not update — reading it would show
    // stale data from an earlier billing visit, per Cursor Bugbot).
    cardPaymentInfo,
    cardPaymentInfoErrors,
    cardPaymentInfoErrorsForBanner,
    ccConsent,
    addressInfo,
    addressInfoErrorsForDisplay,
    addressInfoErrorsForBannerDisplay,
    submitInfo,
  } = useSelector(({ billingInformation }) => billingInformation);

  const [elementRefInfo, setElementRefInfo] = useState();
  // #genai "Make default payment method" — rendered on desktop per Figma; hidden on mobile (CSS).
  const [isDefaultMethod, setIsDefaultMethod] = useState(true);

  // #genai Guards the one-time post-save redirect (the success modal was removed — we now go
  // straight to the /billing-information list, which shows the green "Payment method added" banner).
  const didNavigateRef = useRef(false);

  // #genai Bugbot "Stale submit state add routes" (HIGH): the mount-time resetAddPaymentFormState
  // dispatch and the redirect useEffect below fire in the same commit phase, so if a prior save
  // left submitInfo.showMessage=true in Redux, the redirect effect can read the STALE closure value
  // (from render #1, before the reset re-renders) and bounce the user straight to /billing-info
  // before they ever type a field. Also protects against any external dispatch that flips
  // showMessage without a real user submit here. didSubmitRef only flips to true inside
  // handleSubmit, so a redirect can only happen for an in-flight submit initiated on this mount.
  const didSubmitRef = useRef(false);

  // #genai Phase 2: NO billing-info GET on this route. Every visit opens a completely fresh form —
  // we wipe all card/ACH/address form slices AND the saved-bank summary (achInfo) on mount, so no
  // previously saved data is shown. Account context needed for submit (initParameters / billing
  // snapshot) already lives in userData from the /billing-information page the user navigates from.
  // The card branch of the submit saga is forced via setSelectedPaymentMethod, and consent defaults
  // to checked (required for the card save to validate, Figma 226:27568).
  useEffect(() => {
    dispatch(resetAddPaymentFormState());
    dispatch(setSelectedPaymentMethod(PAYMENT_METHOD_OPTION_IDS.Card));
    dispatch(updateCreditCardConsent(true));
  }, [dispatch]);

  // #genai Deep-link hydration (separate from the form reset above so it never re-fills the form):
  // fetch account context only when billPayerId is missing.
  useEffect(() => {
    if (!billPayerId) {
      dispatch(loadBillingAccountContext());
    }
  }, [dispatch, billPayerId]);

  // #genai On a successful save, skip the confirmation modal and redirect straight to
  // /billing-information, carrying the success details so the list shows the green "Payment
  // method added" banner (Figma 331:26943). Guarded so it fires exactly once. Data source:
  //   - accountTypeLabel: CREDIT_CARD_NAMES[cardPaymentInfo.cardType] from the §2.6 save
  //     response (creditCardType — e.g. 2 → "Visa"), NOT what the microform detected on the
  //     client. Sourced from `cardPaymentInfo` (populated by the toggle-user submit saga path
  //     via updateCardPaymentInfo) rather than the legacy `paymentInfo` slice, which the
  //     toggle path does not update — reading `paymentInfo` here would show stale/incorrect
  //     data from an earlier billing visit for Phase 2 ACH-eligible users.
  //   - accountSuffix:    last 4 chars of cardPaymentInfo.cardNumber (the server-returned
  //     masked PAN, e.g. "411111XXXXXX1111" → "1111"), NOT the raw PAN (never exposed
  //     post-tokenize).
  //   - isDefault:        the user's actual "Make default" checkbox state at submit time. The
  //     "Set as your default…" line on the success banner is gated on this and only renders
  //     when the user left the checkbox ticked.
  // The default checkbox is shown on mobile too, so the banner reflects the user's actual choice
  // on every breakpoint.
  // #genai Handoff via Redux (setRecentlyAddedPaymentMethod), mirroring the ACH page — see the
  // comment on AddAchPaymentMethod's redirect effect for why history.push state was unreliable.
  useEffect(() => {
    // #genai didSubmitRef gates on "user actually clicked Submit on this mount". Without it, a
    // stale showMessage=true from a prior save would race the mount reset and trigger an immediate
    // redirect before the reducer's BILLING_INFO_RESET_ADD_PAYMENT_FORM commit re-renders us.
    if (
      didSubmitRef.current
      && submitInfo.showMessage
      && !submitInfo.errorMessage
      && !isLoading
      && !didNavigateRef.current
    ) {
      didNavigateRef.current = true;
      const savedMaskedPan = cardPaymentInfo?.cardNumber || '';
      const savedCardType = cardPaymentInfo?.cardType;
      dispatch(setRecentlyAddedPaymentMethod({
        accountTypeLabel: CREDIT_CARD_NAMES[savedCardType] || '',
        accountSuffix: savedMaskedPan.slice(-4),
        isDefault: isDefaultMethod,
      }));
      dispatch(resetBillingSubmission());
      history.push(BILLING_INFORMATION_ROUTE);
    }
  }, [submitInfo.showMessage, submitInfo.errorMessage, isLoading]);

  // #genai Bugbot "Phone-required contract mismatch" (also protects every other BillToXxxMissing
  // backend rejection): when the submit saga surfaces a per-field required error we route it
  // onto the SAME inline addressInfoErrors slice per-keystroke validation writes to, then reset
  // submitInfo so the generic SomethingWentWrongDialog stays closed. The user sees an asterisk-
  // red field label, helper text under the input, and a line item in the ValidationMessageBanner
  // — behaviourally identical to a client-side required error. Mirrors the AddAch page so both
  // Phase 2 Add-Payment routes have identical backend-error-to-inline-field routing.
  useEffect(() => {
    const inline = getBillToInlineFieldError(submitInfo, t);
    if (inline) {
      dispatch(updateAddressInfoErrors(inline.fieldId, inline.fieldError, inline.bannerError));
      dispatch(updateAddressValidationErrorsForDisplay());
      dispatch(resetBillingSubmission());
    }
  }, [submitInfo.errorType, submitInfo.errorCode]);

  // #genai Bugbot "BillToFirstNameMissing / BillToLastNameMissing fall through to generic dialog":
  // First / Last name inputs on the Phase 2 Add-Card page live on the cardPaymentInfo slice (not
  // addressInfo), so the address routing above cannot cover them. Mirror the AddAch payment-info
  // effect so both Phase 2 Add-Payment routes have identical backend-error-to-inline-field
  // routing for the name fields the microform owns. See AddAchPaymentMethod for the full
  // rationale (whole-slice reducer action → include current errors in deps + null-guard idempotent).
  useEffect(() => {
    const inlinePayment = getBillToInlinePaymentFieldError(submitInfo, t);
    if (inlinePayment) {
      dispatch(updateCardPaymentInfoErrors({
        ...cardPaymentInfoErrors,
        [inlinePayment.fieldId]: inlinePayment.fieldError,
      }));
      dispatch(updateCardPaymentInfoErrorsForBanner({
        ...cardPaymentInfoErrorsForBanner,
        [inlinePayment.fieldId]: inlinePayment.bannerError,
      }));
      dispatch(resetBillingSubmission());
    }
  }, [submitInfo.errorType, submitInfo.errorCode, cardPaymentInfoErrors, cardPaymentInfoErrorsForBanner]);

  // #genai Partial dispatch (mirrors the ACH page): the reducer's spread-merge applies each field
  // update on top of the current Redux state, so back-to-back handleChange calls from
  // PaymentInformation (e.g. cardType detection followed by field edits) compose atomically
  // without stale-snapshot races. No component-side "latest" ref is needed.
  // #genai First / Last name Cybersource rules run on Add Payment Method, same as Company.
  const handleCardPaymentInfoChange = useCallback((fieldId, event) => {
    const { value } = event.target;
    const targetId = fieldId || event.target.id;
    dispatch(updateCardPaymentInfo({ [targetId]: value, isUpdated: true }));
  }, [dispatch]);

  const handleConsentCheckboxChange = () => dispatch(updateCreditCardConsent(!ccConsent));
  const handleAddressInfoChange = (fieldId, value) => dispatch(updateAddressInfo(fieldId, value));
  const handleAddressInfoErrorChange = (fieldId, fieldErrorValue, bannerErrorValue) => dispatch(
    updateAddressInfoErrors(fieldId, fieldErrorValue, bannerErrorValue),
  );

  const updateElementRefInfo = (componentRefInfo) => {
    setElementRefInfo((prevState) => buildElementRefInfoState(prevState, componentRefInfo));
  };

  // #genai Bugbot "Stale submit state add routes" belt-and-suspenders: the mount-time
  // resetAddPaymentFormState already zeroes submitInfo, but explicitly clearing on cancel
  // guarantees a partially-completed submit slice never survives the trip back to
  // /billing-information (e.g., cancel while the SUBMIT_ERROR dialog is open).
  const handleCancel = () => {
    dispatch(resetBillingSubmission());
    history.push(BILLING_INFORMATION_ROUTE);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    // #genai Mark that a submit was initiated on THIS mount so the redirect useEffect below can
    // distinguish a real success from a stale submitInfo.showMessage=true left over from a prior
    // billing save (see Bugbot "Stale submit state add routes").
    didSubmitRef.current = true;
    const transientTokenData = paymentInfoRef.current ? await paymentInfoRef.current.tokenize() : {};
    // #genai isPhase2Card scopes the Phase 2 Add-Card required-field validation guard (Figma
    // 885:10004) to this dedicated route only, so Phase 1 card saves keep the touch-based
    // address validation gate untouched. isDefault carries the "Make default" checkbox through to
    // the §2.6 card body (cardData.isDefault).
    dispatch(submitBillingInfo({ ...transientTokenData, isPhase2Card: true, isDefault: isDefaultMethod }));
  };

  // #genai Wait for billing eligibility; redirect only after it is known (flag off / ineligible).
  if (routeAccess === ADD_PAYMENT_ROUTE_ACCESS.PENDING) {
    return <AddPaymentMethodLoader />;
  }

  if (routeAccess === ADD_PAYMENT_ROUTE_ACCESS.DENIED) {
    return <Redirect to={BILLING_INFORMATION_ROUTE} />;
  }

  // #genai Keep the validation-banner messages in sync with the latest field errors.
  if (shouldUpdateElementRefMessages(elementRefInfo, addressInfoErrorsForBannerDisplay, cardPaymentInfoErrorsForBanner)) {
    setElementRefInfo((prevState) => updateMessages(prevState, addressInfoErrorsForBannerDisplay, cardPaymentInfoErrorsForBanner));
  }

  // #genai Phase 2 (Figma 732:26114 / 269:22087): backend submit failures on the Add-Card page
  // surface via the same modal dialogs the Add-Bank page uses — "Payment method already exists"
  // for a duplicate, "Something went wrong" for everything else (with the supportReference on
  // 503 PersistenceFailedAfterTokenize). Client-side field validation still uses
  // ValidationMessageBanner at the top of the form; the two signals coexist. The dialog is
  // dismissed by clearing submitInfo via resetBillingSubmission; the duplicate variant's
  // "Return to Payment Methods (Billing)" navigates back to the list.
  const submitErrorDialog = isLoading ? null : getSubmitErrorDialogKind(submitInfo);
  const dismissSubmitError = () => dispatch(resetBillingSubmission());
  const returnToBillingList = () => {
    dispatch(resetBillingSubmission());
    history.push(BILLING_INFORMATION_ROUTE);
  };

  return (
    <>
      {!isLoading && showValidationBanner(cardPaymentInfoErrorsForBanner, addressInfoErrorsForBannerDisplay) && (
        <ValidationMessageBanner
          title={t('validation_message_banner__title')}
          heading={t('validation_message_banner__error_heading')}
          elementRefInfo={elementRefInfo}
        />
      )}
      <PaymentMethodExistsDialog
        open={submitErrorDialog === 'duplicate'}
        onOk={dismissSubmitError}
        onReturn={returnToBillingList}
      />
      <SomethingWentWrongDialog
        open={submitErrorDialog === 'generic' || submitErrorDialog === 'orphan'}
        onOk={dismissSubmitError}
        supportReference={submitErrorDialog === 'orphan' ? submitInfo.supportReference : null}
      />
      <form onSubmit={handleSubmit} className={classes.formRoot}>
        <EVGrid className={classes.card}>
          {/* #genai Mobile-only sub-header (Figma 269:21589): back arrow + centered "Add Payment
              Method" title (no close X per the new design). Hidden on desktop via CSS; desktop chrome
              already provides page context. */}
          <div className={classes.mobileHeader}>
            <button
              type="button"
              className={classes.mobileHeaderIconBtn}
              onClick={handleCancel}
              aria-label={t('billing_information__add_payment_method_back')}
            >
              <EVArrowBackIcon />
            </button>
            <EVTypography className={classes.mobileHeaderTitle}>
              {t('billing_information__add_payment_method')}
            </EVTypography>
          </div>

          <EVGrid className={classes.sections}>
            {/* #genai Mobile grouper (Figma 269:21589) — display: contents on desktop so the
                existing layout is unchanged; flex group with 16/24 padding + bottom hairline
                on mobile, holding the Payment Information section. */}
            <div className={classes.mobilePaymentGroup}>
              {/* ── Section 1: Payment Information ── */}
              <EVTypography className={classes.sectionHeader}>
                {t('billing_information__ach_payment_information_step')}
              </EVTypography>
              <EVGrid className={`${classes.subSection} ${classes.paymentSectionPad}`}>
                <PaymentInformation
                  // #genai Bugbot "Submit error remounts payment form" (MEDIUM): keyed on
                  // submitInfo.showMessage alone, this remounts the microform for BOTH success
                  // and failure. On a submit failure the SomethingWentWrong / duplicate dialog
                  // is open — remounting tears down the CyberSource iframes and wipes the card
                  // number the user typed. Success already redirects away (see the useEffect
                  // above), so the "submitted" key value is only reachable on true success —
                  // this predicate makes the failure path a no-op.
                  key={submitInfo.showMessage && !submitInfo.errorMessage ? 'card-submitted' : 'card-initial'}
                  isLoading={isLoading}
                  paymentInfo={cardPaymentInfo}
                  handleChange={handleCardPaymentInfoChange}
                  ccConsent={ccConsent}
                  handleConsentCheckboxChange={handleConsentCheckboxChange}
                  paymentInfoErrors={cardPaymentInfoErrors}
                  ref={paymentInfoRef}
                  displayCardChargeMessage
                  updateElementRefs={updateElementRefInfo}
                  hideNameFields
                  semiBoldLabels
                  splitCardContainer
                />
              </EVGrid>
            </div>

            {/* #genai Mobile grouper (Figma 226:31587) for Billing Address + default checkbox. */}
            <div className={classes.mobileBillingGroup}>
              {/* ── Section 2: Billing Information ── */}
              <EVGrid className={classes.billingHeaderBlock}>
                <EVTypography className={classes.sectionHeader}>
                  {t('billing_information__ach_billing_information')}
                </EVTypography>
                <EVTypography className={classes.billingSubtitle}>
                  {t('billing_information__add_card_billing_information_subtitle')}
                </EVTypography>
              </EVGrid>
              {/* #genai First + Last name moved into Billing (Figma 269:21301). */}
              {!isLoading && (
                <EVGrid className={classes.subSection}>
                  {/* #genai Phase 2 field-error UX: labels keep default color on error — only the
                      input box border/fill and helper text below turn red. */}
                  <div className={classes.nameRow}>
                    <EVGrid item>
                      <TextInputField
                        inputId="firstName"
                        inputValue={cardPaymentInfo.firstName}
                        size="small"
                        handleChange={(event) => handleCardPaymentInfoChange('firstName', event)}
                        inputLabelProps={{
                          className: classes.nameLabel,
                          classes: { asterisk: classes.asterisk },
                          required: true,
                        }}
                        inputFieldProps={{
                          fullWidth: true,
                          inputProps: { maxLength: 64 },
                          classes: { root: classes.nameInputText },
                          FormHelperTextProps: { classes: { error: classes.nameInputHelperText } },
                          inputRef: elementRefInfo?.firstName?.elementRef,
                          autoComplete: 'off',
                        }}
                        labelText={t('payment_information__first_name')}
                        helperText={cardPaymentInfoErrors.firstName}
                      />
                    </EVGrid>
                    <EVGrid item>
                      <TextInputField
                        inputId="lastName"
                        inputValue={cardPaymentInfo.lastName}
                        size="small"
                        handleChange={(event) => handleCardPaymentInfoChange('lastName', event)}
                        inputLabelProps={{
                          className: classes.nameLabel,
                          classes: { asterisk: classes.asterisk },
                          required: true,
                        }}
                        inputFieldProps={{
                          fullWidth: true,
                          inputProps: { maxLength: 64 },
                          classes: { root: classes.nameInputText },
                          FormHelperTextProps: { classes: { error: classes.nameInputHelperText } },
                          inputRef: elementRefInfo?.lastName?.elementRef,
                          autoComplete: 'off',
                        }}
                        labelText={t('payment_information__last_name')}
                        helperText={cardPaymentInfoErrors.lastName}
                      />
                    </EVGrid>
                  </div>
                </EVGrid>
              )}
              <EVGrid className={`${classes.subSection} ${classes.addressWrap}`}>
                <AddressInformation
                  isLoading={isLoading}
                  addressInfo={addressInfo}
                  handleChange={handleAddressInfoChange}
                  addressInfoErrors={addressInfoErrorsForDisplay}
                  handleErrorChange={handleAddressInfoErrorChange}
                  onCancel={handleCancel}
                  updateElementRefs={updateElementRefInfo}
                  disableCompanyCountry={false}
                  isPhase2AddPayment
                />
              </EVGrid>

              {/* Make default payment method */}
              {!isLoading && (
                <EVGrid className={classes.defaultCheckbox}>
                  <LabelledCheckbox
                    checkboxId="makeDefaultPaymentMethod"
                    checked={isDefaultMethod}
                    checkboxValue={isDefaultMethod}
                    handleCheckboxSelectChange={() => setIsDefaultMethod(!isDefaultMethod)}
                    checkboxLabel={t('billing_information__make_default_payment_method')}
                  />
                </EVGrid>
              )}
            </div>
          </EVGrid>

          {/* ── Footer ── */}
          {!isLoading && (
            <EVGrid className={classes.footer}>
              <EVButton
                id="add-card-submit-btn"
                type="submit"
                variant="contained"
                className={classes.addButton}
                // #genai Bugbot "Submit before billPayerId loads": on a deep link the account
                // context is fetched by loadBillingAccountContext AFTER mount, so billPayerId can
                // still be undefined when the user finishes the form. Blocking Save until it
                // hydrates prevents a PUT /payment-methods without a billPayerId. In the normal
                // /billing-information → /add-card navigation the value is already present, so the
                // added clause is a no-op there.
                disabled={!cardPaymentInfo.isUpdated || !billPayerId}
              >
                {/* #genai Same label on mobile and desktop (Figma: "Add Payment Method"). */}
                {t('billing_information__add_payment_method')}
              </EVButton>
              <EVButton
                id="add-card-cancel-btn"
                variant="outlined"
                className={classes.cancelButton}
                onClick={handleCancel}
              >
                {t('billing_information__add_payment_method_cancel')}
              </EVButton>
            </EVGrid>
          )}
        </EVGrid>
      </form>
    </>
  );
}

export default AddCardPaymentMethod;
